import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { statsAPI } from "@/services/api";
import type { Stats, StatsParams } from "@/types/stats";

interface StatsState {
  monthlyStats: Stats | null;
  yearlyStats: Stats | null;
  monthlyStatus: "idle" | "loading" | "succeeded" | "failed";
  yearlyStatus: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  monthlyParams: StatsParams | null;
  yearlyParams: StatsParams | null;
}

const initialState: StatsState = {
  monthlyStats: null,
  yearlyStats: null,
  monthlyStatus: "idle",
  yearlyStatus: "idle",
  error: null,
  monthlyParams: null,
  yearlyParams: null,
};

// Async thunk to fetch monthly stats
export const fetchMonthlyStats = createAsyncThunk(
  "stats/fetchMonthlyStats",
  async (params: StatsParams = {}) => {
    const response = await statsAPI.getStats(params);
    return { data: response.data, params };
  }
);

// Async thunk to fetch yearly stats
export const fetchYearlyStats = createAsyncThunk(
  "stats/fetchYearlyStats",
  async (params: StatsParams = {}) => {
    const response = await statsAPI.getStats(params);
    return { data: response.data, params };
  }
);

const statsSlice = createSlice({
  name: "stats",
  initialState,
  reducers: {
    clearStats: (state) => {
      state.monthlyStats = null;
      state.yearlyStats = null;
      state.monthlyStatus = "idle";
      state.yearlyStatus = "idle";
      state.error = null;
      state.monthlyParams = null;
      state.yearlyParams = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Monthly stats
      .addCase(fetchMonthlyStats.pending, (state) => {
        state.monthlyStatus = "loading";
        state.error = null;
      })
      .addCase(fetchMonthlyStats.fulfilled, (state, action) => {
        state.monthlyStatus = "succeeded";
        state.monthlyStats = action.payload.data;
        state.monthlyParams = action.payload.params;
      })
      .addCase(fetchMonthlyStats.rejected, (state, action) => {
        state.monthlyStatus = "failed";
        state.error = action.error.message || "Failed to fetch monthly stats";
      })
      // Yearly stats
      .addCase(fetchYearlyStats.pending, (state) => {
        state.yearlyStatus = "loading";
        state.error = null;
      })
      .addCase(fetchYearlyStats.fulfilled, (state, action) => {
        state.yearlyStatus = "succeeded";
        state.yearlyStats = action.payload.data;
        state.yearlyParams = action.payload.params;
      })
      .addCase(fetchYearlyStats.rejected, (state, action) => {
        state.yearlyStatus = "failed";
        state.error = action.error.message || "Failed to fetch yearly stats";
      });
  },
});

export const { clearStats } = statsSlice.actions;
export default statsSlice.reducer;
